import React from 'react';
import { useBookingLimit } from 'hooks/marketing/useBookingLimit';
import AmberCallout from './AmberCallout';
import { findRule, formatResetAt, isPausedByLimit } from './helpers';

interface Props {
    /** 点「Raise the number →」跳到 Patient limits chip */
    onRaise: () => void;
}

/**
 * Performance chip 顶部的「已触顶」黄条（原型 #perfLimitCallout）。
 *
 * 为什么上限的提示要出现在 Performance 上：provider 习惯在这一屏确认自己在不在线上，
 * 所以暂停这件事不能只在它自己的 chip 里说（原型标注 :1142）。
 *
 * 自己发请求而不是由父级透传：Performance 和 Patient limits 两个 chip 是互斥渲染的，
 * 同一时刻只有一个挂载，不会产生重复请求。
 * Practice View 不用它 —— 原型里 practice 的 Performance 不在本次范围内。
 */
const LimitReachedCallout: React.FC<Props> = ({ onRaise }) => {
    const [data] = useBookingLimit();
    const rule = findRule(data?.rules);

    if (!data || !isPausedByLimit(data.listingState) || !rule) {
        return null;
    }

    const used = rule.usedCount ?? 0;
    const cap = rule.limitCount ?? 0;
    const resetAt = formatResetAt(rule.periodEndAt, data.timezone);
    // 原型 reachedSentence()：人数加粗，超限时改说「over your weekly limit of N」
    const who = used === 1 ? '1 new patient' : `${used} new patients`;
    const verb = used === 1 ? 'has' : 'have';
    const tail = used > cap ? `which is over your weekly limit of ${cap}` : 'which is your weekly limit';

    return (
        <AmberCallout action={{ label: 'Raise the number →', onClick: onRaise }}>
            Your profile is hidden from marketplace search: <strong>{who}</strong> {verb} booked you
            this week, {tail}. It shows again {resetAt}.
        </AmberCallout>
    );
};

export default LimitReachedCallout;
